/* eslint-disable local/ensure-wac-token-usage */
import { useEffect, useState } from 'react';

import { css } from '@compiled/react';

import { media } from '@atlaskit/primitives';

import { ColumnLayout } from '../libraries/design/foundations';
import {
	WCL_GRID_PADDING_SM,
	WCL_GRID_PADDING_LG,
} from '../libraries/design/foundations/grid/src/base';

import { getLocalStorage, removeLocalStorage, setLocalStorage } from './localstorage';

export type GridOverlayFunction = () => void;
/**
 * @description Displays the responsive grid for development/design purposes.
 * Call toggleGrid() in the browser console to display/hide.
 * This component should only be rendered in non-production environments.
 * @returns
 */
export const GridOverlayWrapper = () => {
	// this grid is rendered client side only and should wait until after hydration
	const [hasHydrated, setHasHydrated] = useState<boolean>(false);
	const [shouldShowGrid, setShouldShowGrid] = useState<boolean>(false);

	useEffect(() => {
		setHasHydrated(true);
		setShouldShowGrid(!!getLocalStorage('shouldShowGridOverlay', true));
	}, []);

	if (typeof window !== 'undefined') {
		// @ts-ignore
		window.toggleGrid = (): void => {
			if (!getLocalStorage('shouldShowGridOverlay', true)) {
				setLocalStorage('shouldShowGridOverlay', 'true');
				setShouldShowGrid(true);
			} else {
				removeLocalStorage('shouldShowGridOverlay');
				setShouldShowGrid(false);
			}
		};
	}

	return <>{hasHydrated && shouldShowGrid && <GridOverlay />}</>;
};

export const GridOverlay = () => {
	const gridOverlayStyle = css({
		position: 'fixed',
		width: '100%',
		insetInlineStart: '50%',
		transform: 'translateX(-50%)',
		height: '100vh',
		zIndex: 99999,
		pointerEvents: 'none',
		backgroundColor: 'rgb(from thistle r g b / .2)',

		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'> div': {
			background: 'rgb(from deepskyblue r g b / .6)',
			opacity: '.2',
			backgroundClip: 'padding-box',
			boxShadow: `inset ${WCL_GRID_PADDING_LG}px 0 0 rgb(from limegreen r g b / .4), inset -${WCL_GRID_PADDING_LG}px 0 0 rgb(from limegreen r g b / .4)`,

			[media.below.sm]: {
				boxShadow: `inset ${WCL_GRID_PADDING_SM}px 0 0 rgb(from limegreen r g b / .4), inset -${WCL_GRID_PADDING_SM}px 0 0 rgb(from limegreen r g b / .4)`,
			},
		},
	});

	const columnStyle = css({
		background: 'tomato',
		height: '100vh',
	});

	const columns = new Array(12)
		.fill('')
		.map((_, index) => <div css={columnStyle} key={`column-${index}`} />);

	return (
		<div css={gridOverlayStyle}>
			<ColumnLayout layout="overlay">{columns}</ColumnLayout>
		</div>
	);
};
